import React, { useEffect, useRef, useState } from 'react';
import { IconButton, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { Close as ClearIcon } from '@mui/icons-material';

type Props = Omit<TextFieldProps, 'value' | 'onChange'> & {
  /** The committed value (from the URL). */
  value: string;
  /** Called with the normalized text once typing pauses, or at once on Enter. */
  onCommit: (value: string) => void;
  /** Must match how the committed value is normalized when it is read back. */
  normalize: (value: string) => string;
  delay?: number;
  /** Accessible name of the clear button; omit to leave the button out. */
  clearLabel?: string;
  startAdornment?: React.ReactNode;
};

/**
 * A text field that keeps its own draft while the user types and commits it
 * after a pause. A change to `value` from outside (back button, "clear all")
 * replaces the draft; the echo of its own commit does not, so keystrokes typed
 * while a commit is in flight are never lost.
 */
const DebouncedTextField: React.FC<Props> = ({
  value,
  onCommit,
  normalize,
  delay = 350,
  clearLabel,
  startAdornment,
  InputProps,
  ...rest
}) => {
  const [draft, setDraft] = useState(value);
  const committed = useRef(value);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  useEffect(() => {
    if (value !== committed.current) {
      committed.current = value;
      setDraft(value);
    }
  }, [value]);

  const commit = (text: string) => {
    const next = normalize(text);
    if (next === committed.current) return;
    committed.current = next;
    commitRef.current(next);
  };

  useEffect(() => {
    const next = normalize(draft);
    if (next === committed.current) return undefined;
    const timer = setTimeout(() => {
      if (next === committed.current) return; // already committed by Enter or blur
      committed.current = next;
      commitRef.current(next);
    }, delay);
    return () => clearTimeout(timer);
  }, [draft, delay, normalize]);

  return (
    <TextField
      {...rest}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit(draft);
      }}
      onBlur={() => {
        // Commit what is pending and show it the way it was understood.
        commit(draft);
        setDraft(normalize(draft));
      }}
      InputProps={{
        ...InputProps,
        startAdornment: startAdornment ? (
          <InputAdornment position="start">{startAdornment}</InputAdornment>
        ) : undefined,
        endAdornment:
          clearLabel && draft ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                edge="end"
                aria-label={clearLabel}
                // Keep focus in the field so the blur handler does not commit
                // the text that is about to be cleared.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setDraft('');
                  commit('');
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
      }}
    />
  );
};

export default DebouncedTextField;
